type DeriveToolStatusParams = {
  statusType: string;
  hasPendingInterruptActions: boolean;
  isCurrentMessage: boolean;
  isStoppingMessage?: boolean;
};

type ToolThreadMessage = {
  id: string;
  role: string;
  content?: readonly unknown[];
};

export type ToolLifecycleState = {
  isRunning: boolean;
  isError: boolean;
  isPending: boolean;
  isStopped: boolean;
};

/**
 * Derive UI status for a tool call during double-texting.
 * - A tool is "running" only if the tool lives in the current logical tail
 *   message.
 * - Older running tools from superseded runs are shown as stopped.
 * - When the user pressed stop for this message, force running-like statuses
 *   to render as stopped immediately.
 */
export function deriveToolLifecycleState({
  statusType,
  hasPendingInterruptActions,
  isCurrentMessage,
  isStoppingMessage = false,
}: DeriveToolStatusParams): ToolLifecycleState {
  const isError = statusType === 'incomplete' && isCurrentMessage;
  const interruptedByNewerRun = statusType === 'incomplete' && !isCurrentMessage;
  const isPending = statusType === 'requires-action' && hasPendingInterruptActions;

  const runningByStatus =
    statusType === 'running' || (statusType === 'requires-action' && !hasPendingInterruptActions);

  const isRunning = runningByStatus && isCurrentMessage && !isStoppingMessage;
  const isStopped =
    (runningByStatus && !isRunning && !isError && !isPending) || interruptedByNewerRun;

  return { isRunning, isError, isPending, isStopped };
}

const OPTIMISTIC_ID_PREFIX = '__optimistic__';

function isOptimisticPlaceholderMessage(message: ToolThreadMessage): boolean {
  return (
    message.role === 'assistant' &&
    message.id.startsWith(OPTIMISTIC_ID_PREFIX) &&
    Array.isArray(message.content) &&
    message.content.length === 0
  );
}

/**
 * assistant-ui appends an optimistic empty assistant message while a run is
 * active and the current tail is non-assistant. For tool lifecycle checks, use
 * the last non-placeholder message as the logical tail.
 */
export function getLogicalTailMessageId(messages: readonly ToolThreadMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message) continue;
    if (isOptimisticPlaceholderMessage(message)) continue;
    return message.id;
  }

  return null;
}
